import padStart from 'lodash/padStart';

import {
  LEVER_ONBOARDING_COPY_UPDATES,
  LEVER_ONBOARDING_RELATIONSHIP_INTENT_ENABLED,
} from 'Lib/ConfigService/levers';
import { getLeverValue } from 'Lib/utils/getLeverValue';

import { mapEventValueForAllInInterestedIn } from 'Containers/AuthOnboarding/utils/allInclusiveShowMe';
import {
  parseAllInDisplayOptions,
  parseAllInGenderToSparks,
} from 'Containers/ModalManager/modals/AllInclusiveGender/utils/allInDialogUtils';

import { SexualOrientation } from './types';

// Auth Analytics docs: https://gotinder.atlassian.net/wiki/x/opiqAQ
export const EVENT_CODE_SELECT_NAME = 1;
export const EVENT_CODE_SELECT_BIRTHDAY = 2;
export const EVENT_CODE_UNDERAGE = 3;
export const EVENT_CODE_UNDERAGE_CHANGE_BIRTHDAY = 4;
export const EVENT_CODE_SELECT_GENDER = 5;
export const EVENT_CODE_CUSTOM_GENDER = 6;
export const EVENT_CODE_SELECT_GENDER_PREFERENCE = 7;
export const EVENT_CODE_ADD_PHOTO = 8;
export const EVENT_CODE_ADD_PHOTO_METHOD = 9;
export const EVENT_CODE_ADD_PHOTO_CAMERA = 10;
export const EVENT_CODE_ADD_PHOTO_CAMERA_ROLL = 11;
export const EVENT_CODE_ADD_PHOTO_CAMERA_PERMISSION = 12;
export const EVENT_CODE_SELECT_EMAIL = 13;
export const EVENT_CODE_SMS_LOGIN_FACEBOOK = 15;
export const EVENT_CODE_EMAIL_MARKETING = 16;
export const EVENT_CODE_ALIBI = 19;
export const EVENT_CODE_SEXUAL_ORIENTATIONS = 20;
export const EVENT_CODE_TINDER_RULES = 21;
export const EVENT_CODE_SHOW_ORIENTATION_ON_PROFILE = 22;
export const EVENT_CODE_SHOW_SAME_ORIENTATION_FIRST = 23;
export const EVENT_CODE_SHOW_GENDER_ON_PROFILE = 24;
export const EVENT_CODE_RELATIONSHIP_INTENT = 32;
export const EVENT_CODE_ALL_IN_GENDER_FEEDBACK = 41;
export const EVENT_CODE_ALL_IN_SHOW_ME_IN_SEARCHES_FOR = 42;
export const EVENT_CODE_ALL_IN_ORIENTATION_FEEDBACK = 43;

const KEY_CONSENTS = 'consents';
const KEY_INTERESTS = 'userInterests';
const RELATIONSHIP_INTENT = 'relationshipIntent';

export const ONBOARDING_SCREENS = {
  birthDate: { birthDate: true, birthDateUnderage: false, birthDateUnderageChange: false },
  consents: { consents: true },
  email: { email: true, allowEmailMarketing: true },
  gender: { gender: true, customGender: false, showGenderOnProfile: true },
  sexualOrientations: { sexualOrientations: true, showOrientationOnProfile: true },
  interestedInGender: { interestedInGender: true, showSameOrientationFirst: false },
  name: { name: true },
  photos: { photos: true },
  userInterests: { userInterests: true },
  [RELATIONSHIP_INTENT]: { [RELATIONSHIP_INTENT]: true },
};

export const ONBOARDING_STEPS = [
  'name',
  'birthDate',
  'gender',
  'sexualOrientations',
  'interestedInGender',
  KEY_INTERESTS,
  RELATIONSHIP_INTENT,
  'photos',
  'email',
  KEY_CONSENTS,
] as const;

/**
 * As part of the Onboarding Copy Updates project, Relationship Intent Step must precede the Key Interests Step.
 * This adjustment aligns with the same lever controlling other changes in the Copy Updates Project.
 */
export const ONBOARDING_STEPS_COPY_UPDATES = [
  'name',
  'birthDate',
  'gender',
  'sexualOrientations',
  'interestedInGender',
  RELATIONSHIP_INTENT,
  KEY_INTERESTS,
  'photos',
  'email',
  KEY_CONSENTS,
] as const;

/**
 * Build the list of onboarding steps when showing the screen-by-screen flow (ie. mobile web)
 *
 * Currently, the intention is to show only the editable fields that we get back from the API.
 *
 * Required fields are handled within the step component itself to determine whether or not the screen
 * can be skipped without providing a value.
 */
export function determineOnboardingSteps({ values = {}, editableFields = {} }, isReactivated) {
  const steps: string[] = [];
  let firstEmptyStep, firstStepToShow;

  // @TODO: guard relationship intent step behind lever
  const isRelationshipIntentEnabled = getLeverValue(LEVER_ONBOARDING_RELATIONSHIP_INTENT_ENABLED);
  const isObCopyUpdatesEnabled = getLeverValue(LEVER_ONBOARDING_COPY_UPDATES);

  const onboardingStepsLeverDependent = isObCopyUpdatesEnabled
    ? ONBOARDING_STEPS_COPY_UPDATES
    : ONBOARDING_STEPS;

  onboardingStepsLeverDependent.forEach((onboardingStep) => {
    const fieldsForStep = ONBOARDING_SCREENS[onboardingStep];
    Object.keys(fieldsForStep).forEach((key) => {
      // Hide steps that can't be edited
      if (editableFields[key]) {
        if (key === onboardingStep) {
          if (key === KEY_INTERESTS) {
            // @TODO, depends on api response, can remove after fully rollout
            if (values?.[key]?.availableInterests) {
              steps.push(key);
            }
          } else if (key === RELATIONSHIP_INTENT) {
            // @TODO: remove lever guard after rollout
            if (isRelationshipIntentEnabled) {
              steps.push(key);
            }
          } else {
            steps.push(key);
          }
        }
        // If it's empty and important, it's the first step
        if (key === KEY_CONSENTS) {
          const consentData = values[key];
          if (consentData && consentData.some((item) => !item.checked) && firstEmptyStep == null) {
            firstEmptyStep = onboardingStep;
          }
        } else if (
          firstEmptyStep == null &&
          fieldsForStep[key] &&
          (values[key] === '' || values[key] == null)
        ) {
          firstEmptyStep = onboardingStep;
        }
      }
    });
  });

  if (isReactivated) {
    [firstStepToShow] = steps; // Show all editable fields when reactivating
  } else if (firstEmptyStep || !steps.length) {
    firstStepToShow = firstEmptyStep;
  } else {
    [firstStepToShow] = steps;
  }

  return {
    steps,
    firstStepToShow,
  };
}

/**
 * Mask all but the first/last characters of a string, or just last for 2-character strings
 *
 * @param  {string} value - string to mask
 * @param  {string} [maskChar] - character for masking the string
 * @return {string} masked string
 */
export function maskMiddle(value: string, maskChar = '*') {
  const matchedResults = /^(.)?(.+?)?(.)?$/.exec(value || '');
  if (matchedResults) {
    const [, first = '', middle = '', last = ''] = matchedResults;
    return `${first}${middle
      .split('')
      .map(() => maskChar)
      .join('')}${last}`;
  }
}

/**
 * Convert a date string to YYYY-MM, where month is rounded to the first month of the quarter
 *
 * @param  {string} value - YYYY-MM-DD date
 * @return {string} YYYY-DD string
 */
export function maskBirthDate(value: string) {
  const [year = '0', month = '1'] = value.split('-');
  const quarterMonth = `${3 * Math.floor((Number(month) - 1) / 3) + 1}`;
  return `${padStart(year, 4, '0')}-${padStart(quarterMonth, 2, '0')}`;
}

/**
 * Masks the username and domain of an email address
 *
 * eg. eugene.hwang@gotinder.com -> e**********g@g******r.com
 *
 * @param  {string} value - email address to mask
 * @return {string} masked email string
 */
export function maskEmail(value: string) {
  const emailMatch = /^([^@]+)@(.*)\.([^.]+)$/.exec(value || '');
  if (!emailMatch) {
    return maskMiddle(value);
  }

  const [, username = '', sld = '', tld = ''] = emailMatch;
  return `${maskMiddle(username)}@${maskMiddle(sld)}.${tld}`;
}

/**
 * Get all orientations selected by the users for analytics.
 * @param sexualOrientations
 * @returns array of orientation ids
 */
export function parseSexualOrientationsToSparks(
  sexualOrientations: SexualOrientation[] | null
): string[] {
  return (
    sexualOrientations
      ?.filter((orientation) => orientation.checked)
      .map((orientation) => orientation.id) ?? []
  );
}

const ONBOARDING_SPARKS_EVENTS = {
  allowEmailMarketing: {
    code: EVENT_CODE_EMAIL_MARKETING,
    eventValue: (value) => (value ? 'true' : 'false'),
  },
  name: {
    code: EVENT_CODE_SELECT_NAME,
  },
  birthDate: {
    code: EVENT_CODE_SELECT_BIRTHDAY,
    eventValue: maskBirthDate,
  },
  birthDateUnderage: {
    code: EVENT_CODE_UNDERAGE,
    eventValue: maskBirthDate,
  },
  birthDateUnderageChange: {
    code: EVENT_CODE_UNDERAGE_CHANGE_BIRTHDAY,
    eventValue: maskBirthDate,
  },
  allInGender: {
    code: EVENT_CODE_SELECT_GENDER,
    eventValue(value) {
      return parseAllInGenderToSparks(value);
    },
  },
  displayGenders: {
    code: EVENT_CODE_SHOW_GENDER_ON_PROFILE,
    eventValue(value) {
      return parseAllInDisplayOptions(value);
    },
  },
  gender: {
    code: EVENT_CODE_SELECT_GENDER,
  },
  allInShowMeInSearchesFor: {
    code: EVENT_CODE_ALL_IN_SHOW_ME_IN_SEARCHES_FOR,
  },
  tinderRules: {
    code: EVENT_CODE_TINDER_RULES,
  },
  customGender: {
    code: EVENT_CODE_CUSTOM_GENDER,
  },
  showGenderOnProfile: {
    code: EVENT_CODE_SHOW_GENDER_ON_PROFILE,
  },
  sexualOrientations: {
    code: EVENT_CODE_SEXUAL_ORIENTATIONS,
    eventValue(value) {
      if (typeof value?.[0] === 'string') {
        return value;
      }
      return parseSexualOrientationsToSparks(value);
    },
  },
  displaySexualOrientations: {
    code: EVENT_CODE_SHOW_ORIENTATION_ON_PROFILE,
    eventValue(value) {
      return parseAllInDisplayOptions(value);
    },
  },
  showOrientationOnProfile: {
    code: EVENT_CODE_SHOW_ORIENTATION_ON_PROFILE,
  },
  interestedInGender: {
    code: EVENT_CODE_SELECT_GENDER_PREFERENCE,
  },
  interestedInGenders: {
    code: EVENT_CODE_SELECT_GENDER_PREFERENCE,
    eventValue(value) {
      return mapEventValueForAllInInterestedIn(value);
    },
  },
  showSameOrientationFirst: {
    code: EVENT_CODE_SHOW_SAME_ORIENTATION_FIRST,
  },
  photos: {
    // Web onboarding only allows one image upload
    code: EVENT_CODE_ADD_PHOTO,
    eventValue(value) {
      return !!value;
    },
  },
  email: {
    code: EVENT_CODE_SELECT_EMAIL,
    eventValue: maskEmail,
  },
  userInterests: {
    code: EVENT_CODE_ALIBI,
  },
  relationshipIntent: {
    code: EVENT_CODE_RELATIONSHIP_INTENT,
  },
};

export function getSparksEventData({
  action,
  attempt = 1,
  editable = true,
  eventVersion = 1,
  name,
  required = false,
  success,
  value,
}) {
  const sparksEvent = ONBOARDING_SPARKS_EVENTS[name];

  if (!sparksEvent) {
    return null;
  }

  const eventCode = sparksEvent.code;
  const eventValue =
    typeof sparksEvent.eventValue === 'function'
      ? sparksEvent.eventValue(value)
      : sparksEvent.eventValue || value;

  const status = success ? 1 : 0;
  const eventData = {
    action,
    attemptNum: attempt != null ? attempt : 1,
    editable,
    eventAction: action,
    eventCode,
    eventValue,
    eventVersion,
    required,
    status,
  };
  return eventCode ? eventData : null;
}

export const ERROR_READ_ONLY_FIELD = 40063;

export const errorCodeMessages = {
  40009: {
    field: 'name',
    id: 'AuthOnboarding.theCharactersAreNotAllowed',
  },
  40010: {
    field: 'name',
    id: 'AuthOnboarding.thisNameIsNotAllowed',
  },
  40012: {
    field: 'photos',
    id: 'invalid photo',
  },
  40038: {
    field: 'email',
    id: 'AuthOnboarding.emailInvalid',
  },
  // TODO we need to translate this ID
  50020: {
    field: 'photos',
    id: 'invalid photo server upload',
  },
};
